import { FileText, Loader2, X } from 'lucide-react';
import { saveRecordingFile } from '../lib/audioRecording';
import { cn } from '../lib/cn';

export type AttachmentState = {
  /** Client-side id — stable across upload lifecycle. */
  id: string;
  name: string;
  size?: number;
  /** Present once the upload has landed on the server. */
  path?: string;
  uploading: boolean;
  error?: string;
  /** Memory-only microphone recovery; never included in message/API payloads. */
  localFile?: File;
};

type Props = {
  attachment: AttachmentState;
  onRemove: () => void;
  onRetry?: () => void;
};

export function AttachmentPill({ attachment, onRemove, onRetry }: Props) {
  const { name, uploading, error, size, localFile } = attachment;
  return (
    <div
      className={cn(
        'inline-flex max-w-full flex-wrap items-center gap-2 rounded-2xl border border-border bg-subtle px-3 py-1 text-[13px]',
        error && 'border-destructive/40 bg-destructive/5',
      )}
    >
      {uploading ? (
        <Loader2 size={13} className="shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <FileText size={13} className="shrink-0 text-muted-foreground" />
      )}
      <span
        className={error ? 'min-w-0 break-words' : 'max-w-[180px] truncate'}
        title={error ?? name}
      >
        {error ? `${name} — ${error}` : name}
      </span>
      {size !== undefined && !uploading && !error && (
        <span className="text-muted-foreground">{formatSize(size)}</span>
      )}
      {localFile && error && (
        <span className="w-full text-muted-foreground">
          Запись пока только в браузере. Повторите загрузку или сохраните файл до закрытия страницы.
        </span>
      )}
      {localFile && error && onRetry && (
        <button
          type="button"
          disabled={uploading}
          onClick={onRetry}
          className="min-h-11 rounded-lg px-3 hover:bg-bubble"
        >
          Повторить
        </button>
      )}
      {localFile && (
        <button
          type="button"
          onClick={() => saveRecordingFile(localFile)}
          className="min-h-11 rounded-lg px-3 hover:bg-bubble"
        >
          Сохранить
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-bubble"
        title="Удалить вложение"
        aria-label={`Удалить вложение ${name}`}
      >
        <X size={12} />
      </button>
    </div>
  );
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
